import React, { useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import moment from '../../lib/moment-timezone';
import classNames from 'classnames';
import { getDateRange, loadMoreTimeframeMessages, timeframeToRange, timeframeToTimeBlock, TimeframeType } from './timeframeUtils'
import { useTimezone } from '../common/withTimezone';

import PostsTimeBlock, { PostsTimeBlockShortformOption } from './PostsTimeBlock';
import { useOnPropsChanged } from '../hooks/useOnPropsChanged';
import { Typography } from "../common/Typography";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles('PostsTimeframeList', (theme: ThemeType) => ({
  loading: {
    opacity: .4,
  },
  loadMore: {
    ...theme.typography.postStyle,
    color: theme.palette.primary.main
  }
}))

// Returns a reference-stable copy of `value` that only changes when `value`
// changes by deep equality, so it can be used as a hook dependency without
// firing on every render when parents pass freshly-built object literals.
function useDeepMemo<T>(value: T): T {
  const ref = useRef(value);
  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

const PostsTimeframeList = ({after, before, timeframe, numTimeBlocks, postListParameters, dimWhenLoading, reverse, shortform, includeTags=true}: {
  after: Date|string,
  before: Date|string,
  timeframe: TimeframeType,
  numTimeBlocks: number,
  postListParameters: PostsViewTerms,
  dimWhenLoading?: boolean,
  reverse?: boolean,
  shortform: PostsTimeBlockShortformOption,
  includeTags: boolean,
}) => {
  const classes = useStyles(styles);
  const { timezone } = useTimezone();
  const [dim,setDim] = useState(dimWhenLoading);
  const [displayedNumTimeBlocks,setDisplayedNumTimeBlocks] = useState(numTimeBlocks ?? 10);
  const [beforeState,setBeforeState] = useState(before);
  const [afterState,setAfterState] = useState(after);

  // `postListParameters` is re-created as a fresh object on every parent
  // render (e.g. opening the settings panel), so depending on it directly
  // with reference equality would make the effect below fire spuriously,
  // re-dimming the list with no actual reload in progress. Stabilize the
  // reference via deep equality so we only react to real changes.
  const stablePostListParameters = useDeepMemo(postListParameters);

  useOnPropsChanged(() => {
    setBeforeState(before);
    setAfterState(after);
    setDim(!!dimWhenLoading);
    setDisplayedNumTimeBlocks(numTimeBlocks);
  }, [before, after, timeframe, stablePostListParameters]);
  
  const loadMoreTimeBlocks = (e: React.MouseEvent) => {
    e.preventDefault();
    const pageSize = numTimeBlocks ?? 10;
    const timeBlock = timeframeToTimeBlock[timeframe]
    const newDisplayedNumTimeBlocks = displayedNumTimeBlocks + pageSize;
    if (reverse) {
      // If the list is reversed, down means going later in time
      const loadMoreBefore = moment(beforeState, 'YYYY-MM-DD')
        .add(numTimeBlocks, timeBlock)
        .format('YYYY-MM-DD');
      setBeforeState(loadMoreBefore);
      setDisplayedNumTimeBlocks(newDisplayedNumTimeBlocks);
    } else {
      // Otherwise, go back earlier in time
      const loadMoreAfter = moment(afterState, 'YYYY-MM-DD')
        .subtract(numTimeBlocks, timeBlock)
        .format('YYYY-MM-DD');
      setAfterState(loadMoreAfter);
      setDisplayedNumTimeBlocks(newDisplayedNumTimeBlocks);
    }
  }

  // Calculating when all the components have loaded looks like a mess of
  // brittleness, we'll just cease to be dim as soon as a single timeBlock has
  // loaded
  const timeBlockLoadComplete = () => {
    if (dim) {
      setDim(false);
    }
  }
  const timeBlock = timeframeToTimeBlock[timeframe]
  const dates = getDateRange(afterState, beforeState, timeBlock)
  const orderedDates = reverse ? dates.reverse() : dates

  const renderLoadMoreTimeBlocks = dates.length && dates.length > 1
  
  return (
    <div className={classNames({[classes.loading]: dim})}>
      {orderedDates.slice(0, displayedNumTimeBlocks).map((date, index) => {
        const startDate = moment.tz(date, timezone);
        const { before, after } = timeframeToRange({startDate, timeBlock, timezone});
        return <PostsTimeBlock
          key={date.toString()+postListParameters?.limit}
          dateForTitle={startDate}
          getTitle={(size) => getTimeBlockTitle(startDate, timeframe, size)}
          before={before} after={after}
          timeframe={timeframe}
          terms={{
            limit: 16,
            ...postListParameters,
          }}
          timeBlockLoadComplete={timeBlockLoadComplete}
          hideIfEmpty={index===0}
          shortform={shortform}
          includeTags={includeTags}
        />
      })}
      {renderLoadMoreTimeBlocks &&
        <Typography
          variant="body1"
          className={classes.loadMore}
          onClick={loadMoreTimeBlocks}
        >
          <a>{loadMoreTimeframeMessages[timeframe]}</a>
        </Typography>
      }
    </div>
  )
};

const isToday = (date: moment.Moment) => date.isSameOrAfter(moment(0, "HH"));

export const getTimeBlockTitle = (
  startDate: moment.Moment,
  timeframe: TimeframeType,
  size: 'xsDown' | 'smUp' | null,
) => {
  if (timeframe === 'yearly') {
    return startDate.format('YYYY');
  }
  if (timeframe === 'monthly') {
    return startDate.format('MMMM YYYY');
  }

  const result = size === 'smUp'
    ? startDate.format('ddd, MMM Do YYYY')
    : startDate.format('dddd, MMMM Do YYYY');
  if (timeframe === 'weekly') {
    return `Week Of ${result}`;
  }
  return result;
}

export default PostsTimeframeList;


